"use client";

import { LoaderCircle } from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useState, type ReactNode } from "react";

import type { TenantCode } from "@/config/tenants";
import { currentUser } from "@/features/auth/services/auth-service";
import { ApiError } from "@/lib/api";

export function GuestOnly({
  children,
  tenantCode,
}: Readonly<{
  children: ReactNode;
  tenantCode: TenantCode;
}>) {
  const router = useRouter();
  const [isGuest, setIsGuest] = useState(false);

  useEffect(() => {
    let active = true;

    void currentUser()
      .then(() => {
        if (!active) return;
        router.replace(`/${tenantCode}/dashboard`);
      })
      .catch((error: unknown) => {
        if (!active) return;

        // An unauthenticated response is the expected state for this page.
        // Other transient failures should not prevent users from signing in.
        if (error instanceof ApiError && error.status !== 401) {
          setIsGuest(true);
          return;
        }

        setIsGuest(true);
      });

    return () => {
      active = false;
    };
  }, [router, tenantCode]);

  if (isGuest) return children;

  return (
    <div className="flex min-h-40 items-center justify-center" aria-busy="true">
      <LoaderCircle className="size-6 animate-spin text-primary" aria-hidden="true" />
      <span className="sr-only">Checking your session</span>
    </div>
  );
}
